import { useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { explainWord, words } from "../lib/api";

const cache = new Map();
const saved = new Set();
export const markSaved = (list) => list.forEach((w) => saved.add(w.toLowerCase()));
export const forgetSaved = (word) => saved.delete(word.toLowerCase());

const SaveWord = ({ word, data }) => {
  const [done, setDone] = useState(saved.has(word.toLowerCase()));
  const save = async () => {
    try {
      await words.save({ word, ...data });
      saved.add(word.toLowerCase());
      setDone(true);
      toast.success(`"${word}" saved to My Words`);
    } catch {
      toast.error("Couldn't save that word. Try again!");
    }
  };
  return (
    <button type="button" data-testid="save-word-btn" onClick={save} disabled={done} className="what-save-word">
      <Star size={14} fill={done ? "currentColor" : "none"} /> {done ? "Saved in My Words" : "Save to My Words"}
    </button>
  );
};

export const WordBalloon = ({ word, context }) => {
  const [data, setData] = useState(cache.get(word.toLowerCase()) || null);
  const [error, setError] = useState("");

  const load = async (open) => {
    if (!open || data) return;
    setError("");
    try {
      const d = await explainWord(word, context.slice(0, 600));
      cache.set(word.toLowerCase(), d);
      setData(d);
    } catch {
      setError("Oops! Try tapping again.");
    }
  };

  const slug = word.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <PopoverPrimitive.Root onOpenChange={load}>
      <PopoverPrimitive.Trigger asChild>
        <button type="button" data-testid={`hard-word-${slug}`} className="what-hard-word">{word}</button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content side="top" sideOffset={8} collisionPadding={12} data-testid="word-balloon" className="what-balloon">
          <p className="font-display text-lg font-bold text-[#0B0F17] capitalize">{word}</p>
          {!data && !error && <p className="flex items-center gap-2 text-sm text-slate-600 mt-2"><Loader2 size={14} className="animate-spin" /> Thinking…</p>}
          {error && <p data-testid="word-balloon-error" className="text-sm text-red-600 mt-2">{error}</p>}
          {data && (
            <div className="space-y-3 mt-2 text-[14px] leading-snug text-slate-800">
              <div>
                <p className="what-balloon-label">What it means here</p>
                <p data-testid="word-meaning">{data.meaning}</p>
              </div>
              <div>
                <p className="what-balloon-label">It can also mean</p>
                <p data-testid="word-alt-meaning">{data.alt_meaning}</p>
              </div>
              {data.example && <p data-testid="word-example" className="italic text-slate-600">"{data.example}"</p>}
              <SaveWord word={word} data={data} />
            </div>
          )}
          <PopoverPrimitive.Arrow className="fill-[#E0F2FE]" width={16} height={8} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};
